import { FC, useEffect, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { ActivityType, IActivityEntry } from "@hulanbv/platformapp";
import { stylesheet } from "typestyle";
import { PageBody } from "../../../elements/page-body.element";
import { Page } from "../../../elements/page.element";
import { activityComponentsService } from "../../../../state/activities/activity-components.service";
import { LoadingSpinner } from "../../../elements/loading-spinner.element";
import { Button } from "../../../elements/button.element";
import { Flex } from "../../../elements/flex.element";
import { activityEntriesService } from "../../../../state/activities/activity-entries.service";
import { authenticationService } from "../../../../state/authentication/authentication.service";
import { routes } from "../../../../state/common/constants/routes.constants";
import { TwoSidesOfTheSameCoinOverviewView } from "./two-sides-of-the-same-coin-overview-view";
import { useInviteInteractionFromQuery } from "../../../../state/exercise-invite-interactions/use-invite-interaction-from-query.hook";

const screenName = "Values and Barriers are 2 Sides of the Same Coin";

const TwoSidesOfTheSameCoinActivityScreen: FC = () => {
  const history = useHistory();
  const exerciseInviteId = useInviteInteractionFromQuery();

  const [activityState, setActivityState] = useState<
    "informing" | "loading" | "playing" | "completed"
  >("loading");

  const [definedObstacles, setDefinedObstacles] = useState<
    IActivityEntry[] | null
  >(null);
  useEffect(() => {
    async function fetchIdentifiedValues() {
      if (!exerciseInviteId) {
        // no exercise invite id, fetch activity components and disguise them as entries
        const { data: components } = await activityComponentsService.getAll({
          match: { activityType: ActivityType.VALUE_IDENTIFICATION },
          random: true,
          limit: 5,
        });
        setDefinedObstacles(
          components.map((component) => ({
            _id: component.id,
            id: component.id,
            value: "The users answered obstacle will be shown here",
            activityType: ActivityType.VALUE_OBSTACLES,
            exerciseInviteId: "",
            input: component.value,
            userId: "",
            sessionIdentifier: "",
          })),
        );
        setActivityState("informing");
        return;
      }

      // get latest value identification session ID
      const { data: valueIdentificationSession } =
        await activityEntriesService.getAll({
          distinct: "sessionIdentifier",
          match: {
            activityType: ActivityType.VALUE_OBSTACLES,
            userId: authenticationService.getSession()?.userId ?? "",
          },
          sort: ["-createdAt"],
          limit: 1,
        });
      const sessionIdentifier =
        valueIdentificationSession?.[0]?.sessionIdentifier ?? null;
      if (sessionIdentifier === null) {
        setDefinedObstacles([]);
        setActivityState("playing");
        return;
      }

      const { data: entries } = await activityEntriesService.getAll({
        match: { sessionIdentifier, value: { $ne: null } },
        sort: ["-createdAt"],
        limit: 5,
      });

      if (!entries || entries.length < 5) {
        setDefinedObstacles([]);
        setActivityState("playing");
        return;
      }

      setActivityState("playing");
      setDefinedObstacles(entries);
    }

    fetchIdentifiedValues();
  }, [exerciseInviteId]);

  if (activityState === "loading") {
    return (
      <Page hideGoBack hideProfile title={screenName} isNavigationDisabled>
        <PageBody>
          <Flex flex={1} justifyContent="center" alignItems="center">
            <LoadingSpinner color="black" />
          </Flex>
        </PageBody>
      </Page>
    );
  }

  if (definedObstacles === null || activityState === "informing") {
    return (
      <Page
        hideGoBack
        hideProfile
        title={screenName}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex flex={1} justifyContent="center" alignItems="center">
            <p style={{ textAlign: "center" }}>
              The exercise was not opened through an exercise invite. You can
              continue the values of the exercise will not be based on your
              previous input.
            </p>
          </Flex>
          <Button
            centerChildren
            attributes={{ onClick: () => setActivityState("playing") }}
          >
            I understand
          </Button>
        </PageBody>
      </Page>
    );
  }

  if (definedObstacles.length < 5) {
    return (
      <Page
        hideGoBack
        hideProfile
        title={screenName}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex
            flex={1}
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <h3 style={{ textAlign: "center", margin: 0 }}>
              Insufficient obstacles defined
            </h3>
            <p style={{ textAlign: "center", margin: 0 }}>
              You need to identify at least 5 values before you can define
              obstacles for them. Please finish the value values card sort
              activity first.
            </p>
            <Button
              centerChildren
              attributes={{
                onClick: () =>
                  history.replace(
                    `${generatePath(
                      routes.valueObstaclesActivity.path,
                    )}?exerciseInviteId=${exerciseInviteId}`,
                  ),
              }}
            >
              Go to activity
            </Button>
          </Flex>
        </PageBody>
      </Page>
    );
  }

  if (activityState === "completed") {
    return (
      <Page
        hideGoBack
        hideProfile
        title={screenName}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex
            flex={1}
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <h3 style={{ textAlign: "center", margin: 0 }}>
              Activity Completed
            </h3>
            <p style={{ textAlign: "center", margin: 0 }}>
              You have completed this activity. You can now close this window.
            </p>
          </Flex>
          {
            // condtionally render a button to close the window if in a popup
            window.opener && (
              <Button
                centerChildren
                attributes={{ onClick: () => window.close() }}
              >
                Close
              </Button>
            )
          }
        </PageBody>
      </Page>
    );
  }

  return (
    <Page
      hideGoBack
      hideProfile
      title={screenName}
      isNavigationDisabled
      className={styles.page}
    >
      <PageBody fullHeight>
        <TwoSidesOfTheSameCoinOverviewView
          onComplete={() => setActivityState("completed")}
          values={definedObstacles}
        />
      </PageBody>
    </Page>
  );
};

export { TwoSidesOfTheSameCoinActivityScreen };

const styles = stylesheet({
  page: {
    background: "#fafafa",
    overflow: "hidden",
  },
});
